// src/components/Tickets/TicketScoreModal.tsx
// ✅ VERSIÓN CORREGIDA - Con campo de cotización USD manual

import { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { 
  X, TrendingUp, Clock, DollarSign, AlertTriangle, 
  Zap, Users, FileText 
} from 'lucide-react';
import { buildApiUrl } from '../../config/api';

interface TicketScoreModalProps {
  ticketId: string;
  ticketTitle: string;
  onClose: () => void;
  onSuccess: (scoreData?: any) => void;
  initialData?: any;
  isRegenerating?: boolean;
}

export default function TicketScoreModal({ 
  ticketId, 
  ticketTitle, 
  onClose, 
  onSuccess,
  initialData,
  isRegenerating = false
}: TicketScoreModalProps) {
  const { isDark } = useTheme();
  const { success, error: showError } = useToast();

  // ✅ ESTADOS DEL FORMULARIO
  const [estimatedHours, setEstimatedHours] = useState(
    initialData?.estimated_hours || 0
  );
  const [difficulty, setDifficulty] = useState(
    initialData?.difficulty || 1
  );
  const [urgency, setUrgency] = useState(
    initialData?.urgency || 1
  );
  const [resourcesRequired, setResourcesRequired] = useState(
    initialData?.resources_required || 1
  );
  
  // 🆕 CAMPO DE COTIZACIÓN USD MANUAL
  const [quoteAmountUsd, setQuoteAmountUsd] = useState(
    initialData?.quote_amount_usd || ''
  );
  
  const [quoteNotes, setQuoteNotes] = useState(
    initialData?.quote_notes || ''
  );
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ CALCULAR SCORE EN TIEMPO REAL (0-120 puntos)
  const calculateScore = () => {
    const score = (
      (parseInt(estimatedHours as any) || 0) * 10 +
      (parseInt(difficulty as any) || 0) * 30 +
      (parseInt(urgency as any) || 0) * 40 +
      (parseInt(resourcesRequired as any) || 0) * 20
    );
    return score;
  };

  const currentScore = calculateScore();
  const scoreStatus = currentScore >= 80 ? 'high_value' : currentScore >= 40 ? 'medium_value' : 'low_value';

  // ✅ MANEJAR SUBMIT
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // VALIDACIONES
      if (estimatedHours <= 0) {
        showError('Las horas estimadas deben ser mayores a 0');
        return;
      }

      if (difficulty < 1 || difficulty > 5) {
        showError('La dificultad debe estar entre 1 y 5');
        return;
      }

      if (urgency < 1 || urgency > 5) {
        showError('La urgencia debe estar entre 1 y 5');
        return;
      }

      if (resourcesRequired < 1 || resourcesRequired > 5) {
        showError('Los recursos requeridos deben estar entre 1 y 5');
        return;
      }

      // 🆕 VALIDAR MONTO USD OBLIGATORIO
      const amountUsd = parseFloat(quoteAmountUsd as any);
      if (!quoteAmountUsd || isNaN(amountUsd) || amountUsd <= 0) {
        showError('Debe ingresar un monto válido en USD mayor a 0');
        return;
      }

      // PREPARAR DATOS
      const evaluationData = {
        estimated_hours: parseInt(estimatedHours as any),
        difficulty: parseInt(difficulty as any),
        urgency: parseInt(urgency as any),
        resources_required: parseInt(resourcesRequired as any),
        quote_amount_usd: amountUsd, // 🆕 MONTO USD MANUAL
        quote_notes: quoteNotes.trim() || `Cotización para: ${ticketTitle}`
      };

      console.log('📊 [EVALUATE] Enviando evaluación:', evaluationData);

      // 🆕 LLAMAR AL ENDPOINT /api/tickets/:id/evaluate
      const token = localStorage.getItem('kikai_token') || localStorage.getItem('token');
      const response = await fetch(
        buildApiUrl(`/tickets/${ticketId}/evaluate`),
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(evaluationData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || 'Error al evaluar ticket');
      }

      const result = await response.json();
      console.log('✅ [EVALUATE] Evaluación exitosa:', result);

      success(`✅ Ticket evaluado: $${amountUsd} USD`);
      onSuccess(result);
      onClose();

    } catch (err: any) {
      console.error('❌ [EVALUATE] Error:', err);
      showError(err.message || 'Error al evaluar ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } rounded-xl border w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
        
        {/* Header */}
        <div className={`sticky top-0 px-6 py-4 border-b ${
          isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
        } flex items-center justify-between z-10`}>
          <div>
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {isRegenerating ? '🔄 Re-evaluar Ticket' : '📊 Evaluar y Cotizar Ticket'}
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              {ticketTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDark 
                ? 'hover:bg-gray-700 text-gray-400' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Preview del Score */}
          <div className={`${
            isDark ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-700' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
          } border rounded-lg p-4 mb-6`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                  Score Calculado
                </p>
                <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-1`}>
                  {currentScore} / 120
                </p>
                <p className={`text-xs ${isDark ? 'text-purple-400' : 'text-purple-600'} mt-1`}>
                  Estado: {scoreStatus === 'high_value' ? '🔴 Alto valor' : scoreStatus === 'medium_value' ? '🟡 Medio valor' : '🟢 Bajo valor'}
                </p>
              </div>
              <TrendingUp className={`w-12 h-12 ${isDark ? 'text-purple-400' : 'text-purple-500'}`} />
            </div>
          </div>

          {/* Formulario */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Horas Estimadas */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <Clock className="w-4 h-4 inline mr-2" />
                Horas Estimadas
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(parseFloat(e.target.value) || 0)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500`}
                placeholder="0.0"
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Peso: x10 puntos
              </p>
            </div>

            {/* Dificultad Técnica */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <Zap className="w-4 h-4 inline mr-2" />
                Dificultad Técnica (1-5)
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={difficulty}
                onChange={(e) => setDifficulty(parseInt(e.target.value) || 1)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                1=Fácil, 3=Normal, 5=Muy difícil • Peso: x30 puntos
              </p>
            </div>

            {/* Urgencia */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Urgencia (1-5)
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={urgency}
                onChange={(e) => setUrgency(parseInt(e.target.value) || 1)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                1=Baja, 3=Normal, 5=Crítica • Peso: x40 puntos
              </p>
            </div>

            {/* Recursos Requeridos */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <Users className="w-4 h-4 inline mr-2" />
                Recursos Requeridos (1-5)
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={resourcesRequired}
                onChange={(e) => setResourcesRequired(parseInt(e.target.value) || 1)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                1=Mínimo, 3=Normal, 5=Muchos recursos • Peso: x20 puntos
              </p>
            </div>

            {/* 🆕 MONTO USD MANUAL */}
            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <DollarSign className="w-4 h-4 inline mr-2" />
                Monto de Cotización (USD) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={quoteAmountUsd}
                onChange={(e) => setQuoteAmountUsd(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500`}
                placeholder="0.00"
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                ⚠️ Campo obligatorio - Ingrese el monto total de la cotización en dólares
              </p>
            </div>

            {/* Notas de Cotización */}
            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-2 ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <FileText className="w-4 h-4 inline mr-2" />
                Notas de la Cotización
              </label>
              <textarea
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                rows={3}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-purple-500`}
                placeholder="Detalles de la cotización, partes incluidas, condiciones, etc..."
              />
            </div>
          </div>

          {/* Ayuda */}
          <div className={`mt-6 ${
            isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
          } border rounded-lg p-4`}>
            <h4 className={`font-semibold text-sm mb-2 ${
              isDark ? 'text-blue-300' : 'text-blue-900'
            }`}>
              💡 Sistema de Evaluación
            </h4>
            <ul className={`text-xs space-y-1 ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
              <li>• El score se calcula automáticamente (máx. 120 puntos)</li>
              <li>• Horas estimadas: ×10 | Dificultad: ×30 | Urgencia: ×40 | Recursos: ×20</li>
              <li>• El monto USD es obligatorio y debe ser ingresado manualmente</li>
              <li>• Score &lt;40: Bajo valor | 40-79: Medio valor | ≥80: Alto valor</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 px-6 py-4 border-t ${
          isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
        } flex items-center justify-end gap-3`}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            } disabled:opacity-50`}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || estimatedHours <= 0 || !quoteAmountUsd}
            className="px-6 py-3 rounded-lg font-medium bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting 
              ? 'Evaluando...' 
              : (isRegenerating ? 'Re-evaluar y Cotizar' : 'Evaluar y Generar Cotización')
            }
          </button>
        </div>
      </div>
    </div>
  );
}